import ApplicationController from "./application_controller"

// Manages the split search bar: left half = Latin mode, right half = Ottoman mode.
// Placeholders and border theme (navy vs burgundy) switch based on active side and input.
export default class extends ApplicationController {
  static targets = [
    "wrapper",
    "input",
    "placeholderLatin",
    "placeholderOttoman",
    "placeholderContainer",
    "searchTypeField",
    "searchButton"
  ]

  static values = {
    placeholderLatin: String,
    placeholderOttoman: String
  }

  initialize() {
    this.initializeSearchField();
  }

  connect() {
    this.activeMode = null // 'latin' | 'ottoman' | null
    this.syncFromInput()
    this.updatePlaceholders()
    this.applySearchButtonTheme()
  }

  // Click on wrapper: determine left vs right half and activate Latin or Ottoman mode.
  detectClickSide(event) {
    const wrapper = this.wrapperTarget
    const input = this.inputTarget
    const rect = wrapper.getBoundingClientRect()
    const clickX = event.clientX - rect.left
    const clickedSide = clickX < rect.width / 2 ? 'latin' : 'ottoman'

    if (this.activeMode !== null && this.activeMode !== clickedSide) {
      input.value = ''
      // Keep all Stimulus input listeners in sync for programmatic changes.
      input.dispatchEvent(new Event('input', { bubbles: true }))
    }

    this.activeMode = clickedSide
    this.applyInputDirection()
    this.applyTheme()
    this.updatePlaceholders()
    this.updateSearchTypeField()
    input.focus()
    // Only move cursor to RTL start when the field is empty (no existing text to preserve selection for).
    // If there's already Ottoman text, the user may be trying to select/edit it — don't interfere.
    if (this.activeMode === 'ottoman' && input.value.trim().length === 0) {
      this.moveCursorToRtlStart(input)
    }
  }

  handleInput() {
    this.syncFromInput()
    this.updatePlaceholders()
  }

  handleBlur() {
    const input = this.inputTarget
    if (input.value.trim().length === 0) {
      this.activeMode = null
      this.wrapperTarget.classList.remove('search-bar-mode-latin', 'search-bar-mode-ottoman')
      this.placeholderLatinTarget.classList.remove('search-bar-placeholder-hidden')
      this.placeholderOttomanTarget.classList.remove('search-bar-placeholder-hidden')
      this.updateSearchTypeField()
    }
  }

  // Search Field Dynamic Font Styling
  initializeSearchField() {
    // Handle both old and new search field classes
    const searchField = document.querySelector('.search-input-field') || document.querySelector('.search-input-field-new');
    if (searchField) {
      searchField.addEventListener('input', this.handleSearchFieldInput.bind(this));
      // Set initial font based on existing value
      this.updateSearchFieldFont(searchField);
    }
  }

  handleSearchFieldInput(event) {
    this.updateSearchFieldFont(event.target);
  }

  syncFromInput() {
    const input = this.inputTarget
    const text = input.value.trim()
    if (text.length === 0) return
    // Detect Arabic-script input to auto-switch search mode to Ottoman.
    // \u0600-\u06FF: Arabic block (core Arabic letters/marks).
    // \u0750-\u077F: Arabic Supplement (additional Arabic letters).
    // \u08A0-\u08FF: Arabic Extended-A (extended Arabic characters).
    // \uFB50-\uFDFF: Arabic Presentation Forms-A (contextual/ligature forms).
    // \uFE70-\uFEFF: Arabic Presentation Forms-B (additional contextual forms).
    const isOttoman = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text)
    this.activeMode = isOttoman ? 'ottoman' : 'latin'
    this.applyInputDirection()
    this.applyTheme()
  }

  applyInputDirection() {
    const input = this.inputTarget
    if (this.activeMode === 'latin') {
      input.setAttribute('dir', 'ltr')
      input.style.textAlign = 'left'
      input.style.direction = 'ltr'
    } else if (this.activeMode === 'ottoman') {
      input.setAttribute('dir', 'rtl')
      input.style.textAlign = 'right'
      input.style.direction = 'rtl'
    }
  }

  // Place cursor at logical start (right side for RTL) so typing starts from the right
  moveCursorToRtlStart(input) {
    input.setSelectionRange(0, 0)
    // Force reflow so browser applies RTL before selection
    requestAnimationFrame(() => {
      input.setSelectionRange(0, 0)
    })
  }

  applyTheme() {
    const wrapper = this.wrapperTarget
    wrapper.classList.remove('search-bar-mode-latin', 'search-bar-mode-ottoman')
    if (this.activeMode === 'latin') {
      wrapper.classList.add('search-bar-mode-latin')
    } else if (this.activeMode === 'ottoman') {
      wrapper.classList.add('search-bar-mode-ottoman')
    }
  }

  updatePlaceholders() {
    const input = this.inputTarget
    const hasText = input.value.length > 0

    if (hasText) {
      this.placeholderLatinTarget.classList.add('search-bar-placeholder-hidden')
      this.placeholderOttomanTarget.classList.add('search-bar-placeholder-hidden')
      return
    }

    if (this.activeMode === 'latin') {
      this.placeholderLatinTarget.classList.add('search-bar-placeholder-hidden')
      this.placeholderOttomanTarget.classList.remove('search-bar-placeholder-hidden')
    } else if (this.activeMode === 'ottoman') {
      this.placeholderLatinTarget.classList.remove('search-bar-placeholder-hidden')
      this.placeholderOttomanTarget.classList.add('search-bar-placeholder-hidden')
    } else {
      this.placeholderLatinTarget.classList.remove('search-bar-placeholder-hidden')
      this.placeholderOttomanTarget.classList.remove('search-bar-placeholder-hidden')
    }
  }

  updateSearchTypeField() {
    if (!this.hasSearchTypeFieldTarget) return
    const field = this.searchTypeFieldTarget
    field.value = this.activeMode || ''
  }

  applySearchButtonTheme() {
    if (!this.hasSearchButtonTarget) return

    this.searchButtonTarget.classList.remove('search-button-script-latin', 'search-button-script-ottoman')
    if (this.activeMode === 'ottoman') {
      this.searchButtonTarget.classList.add('search-button-script-ottoman')
      return
    }

    if (this.activeMode === 'latin') {
      this.searchButtonTarget.classList.add('search-button-script-latin')
      return
    }

    // Default (initial/empty) button state should be burgundy.
    this.searchButtonTarget.classList.add('search-button-script-ottoman')
  }

  updateSearchFieldFont(field) {
    const text = field.value.trim();
    
    // Remove existing classes
    field.classList.remove('search-field-ottoman', 'search-field-latin');
    
    // Only apply styling if field has content
    if (text.length > 0) {
      const isOttomanText = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text);
      
      if (isOttomanText) {
        // Add Ottoman styling class
        field.classList.add('search-field-ottoman');
      } else {
        // Add Latin styling class
        field.classList.add('search-field-latin');
      }
    }
  }

  handleSearchButtonClick(event) {
    if (!this.hasInputTarget) return
    if (this.inputTarget.value.trim().length === 0) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }
}
